/* 

Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.

*/

import useAbortController from '../common/hooks/useAbortController';
import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
  Dispatch,
  SyntheticEvent,
  useCallback,
  useReducer,
  useEffect,
} from 'react';
import { useAuthContext } from './AuthContext';
import type {
  SCMORecord,
  SCMNRecord,
  EditablePoolFields,
  EditableLoanFields,
  PendingPoolEdits,
  PendingLoanEdits,
} from '../types/GNMAReportingTypes';
import axiosCall from '../services/axios';
import dayjs from 'dayjs';

type GnmaReportingValue = {
  poolRecords: SCMNRecord[];
  setPoolRecords: React.Dispatch<React.SetStateAction<SCMNRecord[]>>;
  loanRecords: SCMORecord[];
  setLoanRecords: React.Dispatch<React.SetStateAction<SCMORecord[]>>;
  pendingPoolEdits: PendingPoolEdits;
  setPendingPoolEdits: React.Dispatch<React.SetStateAction<PendingPoolEdits>>;
  pendingLoanEdits: PendingLoanEdits;
  setPendingLoanEdits: React.Dispatch<React.SetStateAction<PendingLoanEdits>>;
  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  searchPools: (reportingDate: string, poolNumbers: string) => Promise<void>;
  searchLoans: (reportingDate: string, poolNumber: string, loanNumbers: string) => Promise<void>;
  updatePoolField: (id: string, field: keyof EditablePoolFields, value: number) => void;
  updateLoanField: (id: string, field: keyof EditableLoanFields, value: number | string) => void;
  savePoolRow: (record: SCMNRecord) => Promise<void>;
  saveAllPools: () => void;
  saveLoanRow: (record: SCMORecord) => Promise<void>;
  saveAllLoans: () => void;
};

const GNMAReportingContext = createContext<GnmaReportingValue | null>(null);

const useGNMAReportingContext = () => {
  const gnmaReportingContext = useContext(GNMAReportingContext);
  if (gnmaReportingContext === undefined) {
    throw new Error('gnmaReportingContext mmust be inside a GNMAReportingProvider');
  }
  return gnmaReportingContext;
};

type gnmaReportingProviderProps = {
  children: ReactNode;
};

function reducer(state, action) {
  switch (action.type) {
    case 'changed_gnmaReportingInfo': {
      return {
        ...action.gnmaReportingInfo,
      };
    }
    case 'changed_property': {
      return {
        ...state,
        ...action.property,
      };
    }
  }
  throw Error('Unkown action: ' + action.type);
}

const GNMAReportingProvider = ({ children }: gnmaReportingProviderProps) => {
  const { get } = useAbortController();
  const { token } = useAuthContext();
  const [statusAlert, setStatusAlert] = useState({
    status: '',
    message: '',
  });
  const [poolRecords, setPoolRecords] = useState<SCMNRecord[]>([]);
  const [loanRecords, setLoanRecords] = useState<SCMORecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingPoolEdits, setPendingPoolEdits] = useState<PendingPoolEdits>({});
  const [pendingLoanEdits, setPendingLoanEdits] = useState<PendingLoanEdits>({});

  const searchPools = useCallback(
    async (reportingDate: string, poolNumbers: string) => {
      if (!token || !reportingDate) return;

      const controller = get();
      const config = { signal: controller.signal };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };

      try {
        setLoading(true);
        const response = await axiosCall(
          {
            url: `live/investor/pool/list/reportingDate/${reportingDate}`,
            method: 'GET',
          },
          config,
          headers,
        );
        if (!response || response?.error) {
          throw new Error('Unable to fetch pool data for reporting date: ' + reportingDate);
        }
        if (poolNumbers.length > 0) {
          const searchPools = poolNumbers.split(',').map((value) => value.trim());
          setPoolRecords(response.filter((record) => searchPools.includes(record.poolNumber)));
        } else {
          setPoolRecords(response);
        }
      } catch (e) {
        console.error(e);
        setStatusAlert({
          status: 'error',
          message: `Error: ${e}`,
        });
      }
      setLoading(false);
    },
    [token, get],
  );

  const searchLoans = useCallback(
    async (reportingDate: string, poolNumber: string, loanNumbers: string) => {
      if (!token || !reportingDate || !poolNumber) return;

      const controller = get();
      const config = { signal: controller.signal };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const url = `live/servicing/reporting/${reportingDate}/pool/${poolNumber}/list`;

      try {
        setLoading(true);
        const response = await axiosCall(
          {
            url: url,
            method: 'GET',
          },
          config,
          headers,
        );
        if (!response || response?.error) {
          throw new Error('Unable to fetch loan data for reporting date: ' + reportingDate + ', pool: ' + poolNumber);
        }

        if (loanNumbers.length > 0) {
          const searchLoans = loanNumbers.split(',').map((value) => value.trim());
          setLoanRecords(response.filter((record) => searchLoans.includes(record.loan)));
        } else {
          setLoanRecords(response);
        }
      } catch (e) {
        console.error(e);
        setStatusAlert({
          status: 'error',
          message: `Error: ${e}`,
        });
      }
      setLoading(false);
    },
    [token, get],
  );

  const updatePoolField = useCallback((id: string, field: keyof EditablePoolFields, value: number) => {
    setPendingPoolEdits((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  }, []);

  const updateLoanField = useCallback((id: string, field: keyof EditableLoanFields, value: number) => {
    setPendingLoanEdits((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  }, []);

  const savePoolRow = useCallback(
    async (record: SCMNRecord) => {
      if (!token) {
        return;
      }

      const edits = pendingPoolEdits[record.poolNumber];

      if (!edits) {
        return;
      }

      const updatedRecord: SCMNRecord = {
        ...record,
        ...edits,
      };

      const controller = get();
      const config = { signal: controller.signal };

      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const reportingDate = dayjs.unix(record.reportingDate).format('YYYYMMDD');

      try {
        setLoading(true);

        const response = await axiosCall(
          {
            url: `live/investor/poolNumber/${record.poolNumber}/reportingDate/${reportingDate}`,
            method: 'POST',
            data: updatedRecord,
          },
          config,
          headers,
        );

        if (!response || response?.error) {
          throw new Error(`Unable to save pool ${record.poolNumber}`);
        }

        setPoolRecords((previousRecords) =>
          previousRecords.map((poolRecord) =>
            poolRecord.poolNumber === record.poolNumber ? updatedRecord : poolRecord,
          ),
        );

        setPendingPoolEdits((previous) => {
          const updatedPendingEdits = { ...previous };
          delete updatedPendingEdits[record.poolNumber];
          return updatedPendingEdits;
        });

        setStatusAlert({
          status: 'success',
          message: `Pool ${record.poolNumber} saved successfully`,
        });
      } catch (error) {
        console.error(error);

        setStatusAlert({
          status: 'error',
          message: `Error: ${error}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [token, get, pendingPoolEdits],
  );

  const saveAllPools = useCallback(() => {
    setLoading(true);
    for (const poolNumber of Object.keys(pendingPoolEdits)) {
      const poolRecord = poolRecords.find((record) => record.poolNumber === poolNumber);
      savePoolRow(poolRecord);
    }
    setLoading(false);
  }, [pendingPoolEdits, savePoolRow, poolRecords]);

  const saveLoanRow = useCallback(
    async (record: SCMORecord) => {
      if (!token) {
        return;
      }

      const edits = pendingLoanEdits[record.servicerLoanNo];

      if (!edits) {
        return;
      }

      const updatedRecord: SCMORecord = {
        ...record,
        ...edits,
      };

      const controller = get();
      const config = { signal: controller.signal };

      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const reportingDate = dayjs.unix(record.reportDate).format('YYYYMMDD');

      try {
        setLoading(true);

        const response = await axiosCall(
          {
            url: `live/servicing/reporting/${reportingDate}/pool/${record.investorPool}/loan/${record.loan}`,
            method: 'POST',
            data: updatedRecord,
          },
          config,
          headers,
        );

        if (!response || response?.error) {
          throw new Error(`Unable to save loan ${record.servicerLoanNo}`);
        }

        setLoanRecords((previousRecords) =>
          previousRecords.map((loanRecord) =>
            loanRecord.servicerLoanNo === record.servicerLoanNo ? updatedRecord : loanRecord,
          ),
        );

        setPendingLoanEdits((previous) => {
          const updatedPendingEdits = { ...previous };
          delete updatedPendingEdits[record.servicerLoanNo];
          return updatedPendingEdits;
        });

        setStatusAlert({
          status: 'success',
          message: `Loan ${record.servicerLoanNo} saved successfully`,
        });
      } catch (error) {
        console.error(error);

        setStatusAlert({
          status: 'error',
          message: `Error: ${error}`,
        });
      } finally {
        setLoading(false);
      }
    },
    [token, get, pendingLoanEdits],
  );

  const saveAllLoans = useCallback(() => {
    setLoading(true);
    for (const loanNumber of Object.keys(pendingLoanEdits)) {
      const loanRecord = loanRecords.find((record) => record.loan === loanNumber);
      saveLoanRow(loanRecord);
    }
    setLoading(false);
  }, [pendingLoanEdits, saveLoanRow, loanRecords]);

  const contextValue = useMemo(
    () => ({
      poolRecords,
      setPoolRecords,
      loanRecords,
      pendingPoolEdits,
      setPendingPoolEdits,
      pendingLoanEdits,
      setPendingLoanEdits,
      loading,
      setLoading,
      setLoanRecords,
      searchPools,
      searchLoans,
      updatePoolField,
      updateLoanField,
      savePoolRow,
      saveAllPools,
      saveLoanRow,
      saveAllLoans,
    }),
    [
      poolRecords,
      setPoolRecords,
      loanRecords,
      pendingPoolEdits,
      setPendingPoolEdits,
      pendingLoanEdits,
      setPendingLoanEdits,
      loading,
      setLoading,
      setLoanRecords,
      searchPools,
      searchLoans,
      updatePoolField,
      updateLoanField,
      savePoolRow,
      saveAllPools,
      saveLoanRow,
      saveAllLoans,
    ],
  );

  return <GNMAReportingContext.Provider value={contextValue}>{children}</GNMAReportingContext.Provider>;
};

export { GNMAReportingProvider, useGNMAReportingContext };
